// Pressure vessels + atmospheric tanks on Jasmine A.
//
// Process context (Production Deck — Crude Oil Train):
//   • V-201 = 1st-stage 3-phase separator (vertical, ~6.5 m T/T, Ø 1800 mm)
//   • V-202 = test separator (vertical, ~4.0 m T/T, Ø 1200 mm)
//   • V-301 = flare KO drum (horizontal, ~4.8 m T/T, Ø 1600 mm)
//   • T-401 = methanol day tank (vertical, 3 courses, Ø 2400 mm, h ~3.6 m)
//
// CMLs are generated from a course/region grid that mirrors API 510 / API 653
// practice: 4 azimuth positions per shell course, both heads, every nozzle for
// vessels; 4 positions per course + bottom plate + roof plate for tanks.

import type { CML, CmlParent, Tank, Vessel, VesselRegion, TankRegion } from "./types";

// ──────────────────────────────────────────────────────────────────────
// Vessel definitions
// ──────────────────────────────────────────────────────────────────────

export const vessels: Vessel[] = [
  {
    id: "v-201",
    platformId: "plt-jsm-a",
    moduleId: "mod-jsm-a-pd",
    tag: "V-201",
    name: "1st-Stage 3-Phase Separator",
    type: "separator",
    orientation: "vertical",
    diameterMm: 1800,
    tanTanMm: 6500,
    mawpMpa: 4.5,
    designTempC: 90,
    material: "SA-516 Gr.70",
    corrosionAllowanceMm: 3.2,
    insulated: true,
    designCode: "ASME VIII Div 1",
    firstService: "2005-06-12",
    lastInternal: "2021-10-08",
    lastExternal: "2024-03-15",
    courses: [
      { no: 1, heightMm: 2200, nominalThicknessMm: 22.0, minimumThicknessMm: 14.5, material: "SA-516 Gr.70" },
      { no: 2, heightMm: 2200, nominalThicknessMm: 22.0, minimumThicknessMm: 14.5, material: "SA-516 Gr.70" },
      { no: 3, heightMm: 2100, nominalThicknessMm: 22.0, minimumThicknessMm: 14.5, material: "SA-516 Gr.70" },
    ],
    heads: [
      { position: "top", type: "ellipsoidal", nominalThicknessMm: 20.0, minimumThicknessMm: 13.0 },
      { position: "bottom", type: "ellipsoidal", nominalThicknessMm: 20.0, minimumThicknessMm: 13.0 },
    ],
    nozzles: [
      { tag: "N1", dnInch: 10, service: "Inlet (from wellhead manifold)", azimuthDeg: 90, axialFrac: 0.72, rating: "300#" },
      { tag: "N2", dnInch: 8, service: "Gas Outlet (top)", azimuthDeg: 0, axialFrac: 1.0, rating: "300#" },
      { tag: "N3", dnInch: 6, service: "Oil Outlet (mid)", azimuthDeg: 180, axialFrac: 0.45, rating: "300#" },
      { tag: "N4", dnInch: 4, service: "Water Outlet (bottom boot)", azimuthDeg: 180, axialFrac: 0.10, rating: "300#" },
      { tag: "N5", dnInch: 2, service: "PSV-201 (top)", azimuthDeg: 270, axialFrac: 0.95, rating: "300#" },
      { tag: "M1", dnInch: 24, service: "Manway", azimuthDeg: 0, axialFrac: 0.55, rating: "300#" },
    ],
  },

  {
    id: "v-202",
    platformId: "plt-jsm-a",
    moduleId: "mod-jsm-a-pd",
    tag: "V-202",
    name: "Test Separator",
    type: "separator",
    orientation: "vertical",
    diameterMm: 1200,
    tanTanMm: 4000,
    mawpMpa: 4.0,
    designTempC: 80,
    material: "SA-516 Gr.70",
    corrosionAllowanceMm: 3.2,
    insulated: true,
    designCode: "ASME VIII Div 1",
    firstService: "2007-04-22",
    lastInternal: "2022-05-04",
    lastExternal: "2024-05-02",
    courses: [
      { no: 1, heightMm: 2000, nominalThicknessMm: 16.0, minimumThicknessMm: 10.5, material: "SA-516 Gr.70" },
      { no: 2, heightMm: 2000, nominalThicknessMm: 16.0, minimumThicknessMm: 10.5, material: "SA-516 Gr.70" },
    ],
    heads: [
      { position: "top", type: "ellipsoidal", nominalThicknessMm: 14.0, minimumThicknessMm: 9.0 },
      { position: "bottom", type: "ellipsoidal", nominalThicknessMm: 14.0, minimumThicknessMm: 9.0 },
    ],
    nozzles: [
      { tag: "N1", dnInch: 6, service: "Inlet", azimuthDeg: 90, axialFrac: 0.7, rating: "300#" },
      { tag: "N2", dnInch: 6, service: "Gas Outlet", azimuthDeg: 0, axialFrac: 1.0, rating: "300#" },
      { tag: "N3", dnInch: 4, service: "Oil Outlet", azimuthDeg: 180, axialFrac: 0.45, rating: "300#" },
      { tag: "N4", dnInch: 3, service: "Water Outlet", azimuthDeg: 180, axialFrac: 0.10, rating: "300#" },
      { tag: "M1", dnInch: 20, service: "Manway", azimuthDeg: 270, axialFrac: 0.6, rating: "300#" },
    ],
  },

  {
    id: "v-301",
    platformId: "plt-jsm-a",
    moduleId: "mod-jsm-a-pd",
    tag: "V-301",
    name: "Flare KO Drum",
    type: "drum",
    orientation: "horizontal",
    diameterMm: 1600,
    tanTanMm: 4800,
    mawpMpa: 2.5,
    designTempC: 60,
    material: "SA-516 Gr.70 LCB",
    corrosionAllowanceMm: 3.2,
    insulated: true,
    designCode: "ASME VIII Div 1",
    firstService: "2005-06-12",
    lastInternal: "2020-11-18",
    lastExternal: "2024-08-20",
    courses: [
      { no: 1, heightMm: 1600, nominalThicknessMm: 18.0, minimumThicknessMm: 11.5, material: "SA-516 Gr.70 LCB" },
      { no: 2, heightMm: 1600, nominalThicknessMm: 18.0, minimumThicknessMm: 11.5, material: "SA-516 Gr.70 LCB" },
      { no: 3, heightMm: 1600, nominalThicknessMm: 18.0, minimumThicknessMm: 11.5, material: "SA-516 Gr.70 LCB" },
    ],
    heads: [
      { position: "top", type: "hemispherical", nominalThicknessMm: 16.0, minimumThicknessMm: 10.5 },
      { position: "bottom", type: "hemispherical", nominalThicknessMm: 16.0, minimumThicknessMm: 10.5 },
    ],
    nozzles: [
      { tag: "N1", dnInch: 8, service: "Inlet from flare header", azimuthDeg: 0, axialFrac: 0.85, rating: "150#" },
      { tag: "N2", dnInch: 12, service: "Gas Outlet to flare stack", azimuthDeg: 0, axialFrac: 0.15, rating: "150#" },
      { tag: "N3", dnInch: 3, service: "Liquid Drain (bottom)", azimuthDeg: 180, axialFrac: 0.5, rating: "150#" },
      { tag: "M1", dnInch: 20, service: "Manway", azimuthDeg: 90, axialFrac: 0.5, rating: "150#" },
    ],
  },
];

// ──────────────────────────────────────────────────────────────────────
// Tank definitions
// ──────────────────────────────────────────────────────────────────────

export const tanks: Tank[] = [
  {
    id: "t-401",
    platformId: "plt-jsm-a",
    moduleId: "mod-jsm-a-ud",
    tag: "T-401",
    name: "Methanol Day Tank",
    diameterMm: 2400,
    heightMm: 3600,
    service: "Methanol storage for injection skid",
    roofType: "cone",
    bottomType: "flat",
    designCode: "API 650 · API 653",
    material: "SA-240 TP304L (stainless)",
    firstService: "2007-08-01",
    lastInternal: "2023-04-12",
    lastExternal: "2024-09-05",
    courses: [
      { no: 1, heightMm: 1200, nominalThicknessMm: 6.0, minimumThicknessMm: 3.0, material: "SA-240 TP304L" },
      { no: 2, heightMm: 1200, nominalThicknessMm: 6.0, minimumThicknessMm: 3.0, material: "SA-240 TP304L" },
      { no: 3, heightMm: 1200, nominalThicknessMm: 5.0, minimumThicknessMm: 3.0, material: "SA-240 TP304L" },
    ],
  },
];

// ──────────────────────────────────────────────────────────────────────
// CML generation — programmatic, mirrors API 510 / 653 grid placement
// ──────────────────────────────────────────────────────────────────────

interface CmlSeed {
  cmlNo: string;
  desc: string;
  nominal: number;
  tMin: number;
  baseline: number;
  cr: number;
  insulated: boolean;
  type: CML["type"];
}

/** Build a CML quickly from a seed + parent reference + base id. */
export function makeEquipmentCml(
  id: string,
  parent: CmlParent,
  seed: CmlSeed,
  baselineDate: string,
  inspections: Array<{ date: string; inspector: string }>,
  buildMeasurements: (
    cmlId: string,
    baseline: number,
    baselineDate: string,
    cr: number,
    inspections: Array<{ date: string; inspector: string }>,
  ) => CML["measurements"],
  classifyStatus: (current: number, tMin: number, rL: number) => CML["calc"]["status"],
): CML {
  const measurements = buildMeasurements(id, seed.baseline, baselineDate, seed.cr, inspections);
  const latest = measurements[measurements.length - 1];
  const prior = measurements[measurements.length - 2];
  const base = measurements[0];

  const yearsBaseline = 6.15;
  const yearsST = 3.1;
  const ltCR = (base.representativeMm - latest.representativeMm) / yearsBaseline;
  const stCR = (prior.representativeMm - latest.representativeMm) / yearsST;
  const effectiveCR = Math.max(stCR, ltCR, 0.001);
  const rL = Math.max(0, (latest.representativeMm - seed.tMin) / effectiveCR);
  let intervalYr = Math.min(rL / 2, 5);
  intervalYr = Math.min(intervalYr, 10);
  const nextDate = new Date(latest.date);
  nextDate.setDate(nextDate.getDate() + Math.round(intervalYr * 365.25));

  // Equipment CMLs use a synthetic drawingId so getDrawingCmls() ignores them.
  const drawingId =
    parent.kind === "piping"
      ? parent.drawingId
      : parent.kind === "vessel"
        ? `vessel:${parent.vesselId}`
        : `tank:${parent.tankId}`;

  return {
    id,
    drawingId,
    parent,
    cmlNo: seed.cmlNo,
    description: seed.desc,
    type: seed.type,
    pdfPosition: { page: 1, x: 0.5, y: 0.5 },
    nominalMm: seed.nominal,
    minimumMm: seed.tMin,
    retirementMm: seed.tMin * 1.1,
    baselineMm: seed.baseline,
    baselineDate,
    isCuiRisk: seed.insulated,
    isInsulated: seed.insulated,
    isDeadLeg: false,
    measurements,
    calc: {
      shortTermCrMmYr: Math.max(stCR, 0),
      longTermCrMmYr: Math.max(ltCR, 0),
      effectiveCrMmYr: effectiveCR,
      remainingLifeYr: Math.min(rL, 99.9),
      nextInspectionDate: nextDate.toISOString().slice(0, 10),
      status: classifyStatus(latest.representativeMm, seed.tMin, rL),
    },
  };
}

/** Build the per-CML seed list (and matching region) for one vessel. */
export function vesselCmlPlan(v: Vessel): Array<{ seed: CmlSeed; region: VesselRegion }> {
  const out: Array<{ seed: CmlSeed; region: VesselRegion }> = [];
  let cmlIdx = 0;
  const azimuths = [0, 90, 180, 270];

  // 4 CMLs per course at 12/3/6/9 clock + 2 axial fractions (top, bottom)
  v.courses.forEach((c) => {
    azimuths.forEach((az) => {
      cmlIdx++;
      // Mid-course axial
      const cr = baseCorrosionRateFor(v, c.no, az);
      const baseline = c.nominalThicknessMm - 0.4 * (c.no - 1);
      out.push({
        seed: {
          cmlNo: `${v.tag}-S${c.no}-${posToClock(az)}`,
          desc: `Shell Course ${c.no} · ${posToClock(az)}`,
          type: "shell",
          nominal: c.nominalThicknessMm,
          tMin: c.minimumThicknessMm,
          baseline,
          cr,
          insulated: v.insulated,
        },
        region: { kind: "shell", courseNo: c.no, azimuthDeg: az, axialFrac: 0.5 },
      });
    });
  });

  // 1 CML per head at apex + 1 at knuckle
  v.heads.forEach((h) => {
    cmlIdx++;
    out.push({
      seed: {
        cmlNo: `${v.tag}-H-${h.position[0].toUpperCase()}-APEX`,
        desc: `${h.position === "top" ? "Top" : "Bottom"} Head · apex`,
        type: "head",
        nominal: h.nominalThicknessMm,
        tMin: h.minimumThicknessMm,
        baseline: h.nominalThicknessMm - 0.3,
        cr: 0.06 + Math.random() * 0.04,
        insulated: v.insulated,
      },
      region: { kind: "head", position: h.position, offsetFrac: 0, azimuthDeg: 0 },
    });
    cmlIdx++;
    out.push({
      seed: {
        cmlNo: `${v.tag}-H-${h.position[0].toUpperCase()}-KNUCK`,
        desc: `${h.position === "top" ? "Top" : "Bottom"} Head · knuckle`,
        type: "head",
        nominal: h.nominalThicknessMm,
        tMin: h.minimumThicknessMm,
        baseline: h.nominalThicknessMm - 0.5,
        cr: 0.08 + Math.random() * 0.06,
        insulated: v.insulated,
      },
      region: { kind: "head", position: h.position, offsetFrac: 0.7, azimuthDeg: 0 },
    });
  });

  // 1 CML per nozzle weld neck
  v.nozzles.forEach((n) => {
    cmlIdx++;
    const baseT = n.dnInch >= 12 ? 14 : n.dnInch >= 6 ? 10 : 7;
    out.push({
      seed: {
        cmlNo: `${v.tag}-${n.tag}`,
        desc: `Nozzle ${n.tag} weld neck · ${n.service}`,
        type: "nozzle",
        nominal: baseT,
        tMin: baseT * 0.55,
        baseline: baseT - 0.2,
        cr: 0.05 + Math.random() * 0.12,
        insulated: v.insulated,
      },
      region: { kind: "nozzle", nozzleTag: n.tag },
    });
  });

  void cmlIdx;
  return out;
}

/** Tank CML plan — 4 azimuth/course + 2 bottom + 2 roof. */
export function tankCmlPlan(t: Tank): Array<{ seed: CmlSeed; region: TankRegion }> {
  const out: Array<{ seed: CmlSeed; region: TankRegion }> = [];
  const azimuths = [0, 90, 180, 270];

  t.courses.forEach((c) => {
    azimuths.forEach((az) => {
      out.push({
        seed: {
          cmlNo: `${t.tag}-S${c.no}-${posToClock(az)}`,
          desc: `Shell Course ${c.no} · ${posToClock(az)}`,
          type: "shell",
          nominal: c.nominalThicknessMm,
          tMin: c.minimumThicknessMm,
          baseline: c.nominalThicknessMm - 0.15 * (c.no - 1),
          cr: 0.02 + Math.random() * 0.04, // stainless = low CR
          insulated: false,
        },
        region: { kind: "shell", courseNo: c.no, azimuthDeg: az, axialFrac: 0.5 },
      });
    });
  });

  // Bottom plate — 4 quadrants
  [0, 90, 180, 270].forEach((az) => {
    out.push({
      seed: {
        cmlNo: `${t.tag}-B-${posToClock(az)}`,
        desc: `Bottom plate · ${posToClock(az)}`,
        type: "bottom",
        nominal: 8,
        tMin: 4,
        baseline: 7.8,
        cr: 0.04 + Math.random() * 0.04,
        insulated: false,
      },
      region: { kind: "bottom", radiusFrac: 0.65, azimuthDeg: az },
    });
  });

  // Roof — 2 quadrants
  [0, 180].forEach((az) => {
    out.push({
      seed: {
        cmlNo: `${t.tag}-R-${posToClock(az)}`,
        desc: `Roof plate · ${posToClock(az)}`,
        type: "roof",
        nominal: 5,
        tMin: 3,
        baseline: 4.9,
        cr: 0.015 + Math.random() * 0.02,
        insulated: false,
      },
      region: { kind: "roof", radiusFrac: 0.6, azimuthDeg: az },
    });
  });
  return out;
}

function posToClock(az: number): string {
  if (az === 0) return "12";
  if (az === 90) return "03";
  if (az === 180) return "06";
  if (az === 270) return "09";
  return `${az}`;
}

/**
 * Baseline corrosion rate varies per service:
 *   V-201 has higher CR near the gas/water interface (course 1 boot) due to CO2/H2S
 *   V-301 has corrosion on bottom run (acid gas condensate)
 */
function baseCorrosionRateFor(v: Vessel, course: number, az: number): number {
  if (v.id === "v-201") {
    if (course === 1 && (az === 180 || az === 90)) return 0.32 + Math.random() * 0.18; // water boot side
    if (course === 1) return 0.18 + Math.random() * 0.08;
    if (course === 3 && az === 0) return 0.22 + Math.random() * 0.12; // gas top
    return 0.08 + Math.random() * 0.06;
  }
  if (v.id === "v-202") {
    if (course === 1) return 0.10 + Math.random() * 0.08;
    return 0.05 + Math.random() * 0.05;
  }
  if (v.id === "v-301") {
    if (az === 180) return 0.24 + Math.random() * 0.12; // bottom run
    return 0.07 + Math.random() * 0.05;
  }
  return 0.06 + Math.random() * 0.05;
}
